/**
 * Stage 0 (readerUids) — the ORDERING of a grade save, as a pure plan.
 *
 * Zero firebase import so the D1 invariant is unit-testable without booting the SDK:
 * `planGradeWrite` returns an ordered list of batches; `batchUpdateGrades` executes
 * them against real writeBatches, committing each in order.
 *
 * D1 invariant:
 *  - Fits one batch (total ops ≤ maxOpsPerBatch): ONE atomic batch — the embedded
 *    class-doc grade update + history + grade mirror subdocs commit all-or-nothing.
 *  - Doesn't fit: history + mirror sets go in EARLY chunks (committed first,
 *    sequentially); the class-doc update is ALONE in the FINAL batch. A crash
 *    mid-run can then only leave orphan mirror/history subdocs — which the reconcile
 *    deletes — never an embedded grade whose readable mirror never landed.
 */

export type PlannedOp =
  | { kind: 'classUpdate'; data: Record<string, unknown> }
  | { kind: 'mirrorSet'; key: string; data: Record<string, unknown> }
  | { kind: 'historySet'; id: string; data: Record<string, unknown> };

export interface GradeWritePlanInput {
  /** The embedded class-doc update (grades.KEY field paths, maybe releasedGrades:{}). */
  classUpdate: Record<string, unknown>;
  /** Grade mirror subdocs (classes/{id}/grades/{key}) — each already carries readerUids. */
  mirrors: Array<{ key: string; data: Record<string, unknown> }>;
  /** gradeHistory subcollection entries. */
  history: Array<{ id: string; data: Record<string, unknown> }>;
}

export function planGradeWrite(
  input: GradeWritePlanInput,
  maxOpsPerBatch = 450,
): PlannedOp[][] {
  const classOp: PlannedOp = { kind: 'classUpdate', data: input.classUpdate };
  const earlyOps: PlannedOp[] = [
    ...input.history.map(h => ({ kind: 'historySet' as const, id: h.id, data: h.data })),
    ...input.mirrors.map(m => ({ kind: 'mirrorSet' as const, key: m.key, data: m.data })),
  ];

  // Common case: the class update (1 op) + everything else fits a single batch.
  if (1 + earlyOps.length <= maxOpsPerBatch) {
    return [[classOp, ...earlyOps]];
  }

  // Giant save: mirrors + history first (chunked, sequential), class update LAST.
  const batches: PlannedOp[][] = [];
  for (let i = 0; i < earlyOps.length; i += maxOpsPerBatch) {
    batches.push(earlyOps.slice(i, i + maxOpsPerBatch));
  }
  batches.push([classOp]);
  return batches;
}
